import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { 
  BookOpen, Plus, Edit, Trash2, Eye, EyeOff, Save, X,
  ChevronDown, ChevronRight, Lock, Globe, Download,
  History, RotateCcw, FileText, Search, GripVertical,
  AlertTriangle, Check, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for categories
const iconOptions = [
  { value: 'Heart', label: 'Heart (Culture/Values)' },
  { value: 'Award', label: 'Award (Quality)' },
  { value: 'Shield', label: 'Shield (Safety)' },
  { value: 'ClipboardList', label: 'Clipboard (Management)' },
  { value: 'Wrench', label: 'Wrench (Trade/Technical)' },
  { value: 'Users', label: 'Users (Partners/Team)' },
  { value: 'BookOpen', label: 'Book (Handbook)' },
  { value: 'FileText', label: 'Document (Policies)' },
];

// Sortable Category Item Component
const SortableCategoryItem = ({ 
  category, 
  sections, 
  expandedCategories, 
  toggleCategory, 
  openSectionModal,
  toggleCategoryPublish,
  openCategoryModal,
  openDeleteModal,
  onSectionReorder,
  openVersionHistory,
  toggleSectionPublish
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const categorySections = sections[category.id] || [];

  return (
    <div ref={setNodeRef} style={style}>
      {/* Category Header */}
      <div className={`flex items-center gap-3 p-4 hover:bg-slate-50 ${isDragging ? 'bg-slate-100' : ''}`}>
        {/* Drag Handle */}
        <button 
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab hover:bg-slate-200 rounded active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="w-5 h-5 text-slate-400" />
        </button>

        <button 
          onClick={() => toggleCategory(category.id)}
          className="p-1 hover:bg-slate-200 rounded"
        >
          {expandedCategories.includes(category.id) 
            ? <ChevronDown className="w-5 h-5 text-slate-500" />
            : <ChevronRight className="w-5 h-5 text-slate-500" />
          }
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-slate-900">{category.name}</h3>
            {category.access_level === 'internal' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                <Lock className="w-3 h-3" />
                Internal
              </span>
            )}
            {!category.is_published && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                <EyeOff className="w-3 h-3" />
                Draft
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">{category.description}</p>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-sm text-slate-400 mr-2">
            {categorySections.length} docs
          </span>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => openSectionModal(category.id)}
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => toggleCategoryPublish(category)}
          >
            {category.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => openCategoryModal(category)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => openDeleteModal('category', category)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Sections List */}
      {expandedCategories.includes(category.id) && (
        <div className="bg-slate-50 border-t border-slate-200">
          {categorySections.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-slate-500 mb-2">No documents in this category</p>
              <Button variant="outline" size="sm" onClick={() => openSectionModal(category.id)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Document
              </Button>
            </div>
          ) : (
            <SortableSectionsList 
              categoryId={category.id}
              sections={categorySections}
              onReorder={onSectionReorder}
              openSectionModal={openSectionModal}
              openVersionHistory={openVersionHistory}
              toggleSectionPublish={toggleSectionPublish}
              openDeleteModal={openDeleteModal}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Sortable Sections List Component (handles DnD context for sections)
const SortableSectionsList = ({ 
  categoryId,
  sections, 
  onReorder,
  openSectionModal,
  openVersionHistory,
  toggleSectionPublish,
  openDeleteModal
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      const newSections = arrayMove(sections, oldIndex, newIndex);
      onReorder(categoryId, newSections);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className="divide-y divide-slate-200">
          {sections.map((section) => (
            <SortableSectionItem 
              key={section.id}
              section={section}
              categoryId={categoryId}
              openSectionModal={openSectionModal}
              openVersionHistory={openVersionHistory}
              toggleSectionPublish={toggleSectionPublish}
              openDeleteModal={openDeleteModal}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

// Sortable Section Item Component
const SortableSectionItem = ({ 
  section, 
  categoryId,
  openSectionModal,
  openVersionHistory,
  toggleSectionPublish,
  openDeleteModal
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`flex items-center gap-3 px-4 py-3 pl-12 hover:bg-slate-100 ${isDragging ? 'bg-slate-200' : ''}`}
    >
      {/* Drag Handle */}
      <button 
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab hover:bg-slate-200 rounded active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
      </button>

      <FileText className="w-4 h-4 text-slate-400" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-800">{section.title}</span>
          <span className="text-xs text-slate-400">v{section.version}</span>
          {!section.is_published && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
              Draft
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Updated {new Date(section.updated_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex items-center gap-1">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => openVersionHistory(section)}
          title="Version History"
        >
          <History className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => toggleSectionPublish(section)}
        >
          {section.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => openSectionModal(categoryId, section)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => openDeleteModal('section', section)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminStandardsPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Navigation visibility toggle
  const [showStandardsInNav, setShowStandardsInNav] = useState(true);
  const [savingNavSettings, setSavingNavSettings] = useState(false);
  
  // Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'FileText',
    access_level: 'public',
    order: 0
  });

  const [sectionForm, setSectionForm] = useState({
    category_id: '',
    title: '',
    content: '',
    version: '1.0',
    order: 0
  });

  // DnD sensors for categories
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    // Check admin authentication using multiple methods
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const adminUser = localStorage.getItem('admin_user');
    
    if (isLoggedIn || adminUser) {
      setIsAuthenticated(true);
      fetchCategories();
      fetchNavSettings();
    } else {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchNavSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/navigation`);
      const data = await res.json();
      setShowStandardsInNav(data.show_standards !== false);
    } catch (error) {
      console.error('Error fetching nav settings:', error);
    }
  };

  const toggleStandardsVisibility = async () => {
    setSavingNavSettings(true);
    try {
      const newValue = !showStandardsInNav;
      const res = await fetch(`${API_URL}/api/site-settings/navigation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_standards: newValue, show_plans: true }),
      });
      if (res.ok) {
        setShowStandardsInNav(newValue);
        toast.success(newValue ? 'Standards menu enabled' : 'Standards menu hidden');
      } else {
        toast.error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating nav settings:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingNavSettings(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/standards/categories?access_level=all&include_unpublished=true`);
      const data = await res.json();
      setCategories(data);
      
      // Fetch sections for all categories
      for (const cat of data) {
        fetchSections(cat.id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
      setLoading(false);
    }
  };

  const fetchSections = async (categoryId) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/sections?category_id=${categoryId}&include_unpublished=true`);
      const data = await res.json();
      setSections(prev => ({ ...prev, [categoryId]: data }));
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Handle category drag end
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = categories.findIndex(c => c.id === active.id);
      const newIndex = categories.findIndex(c => c.id === over.id);
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      
      // Optimistically update UI
      setCategories(newCategories);
      
      // Save to backend
      try {
        const reorderData = newCategories.map((cat, index) => ({
          id: cat.id,
          order: index + 1
        }));
        
        const res = await fetch(`${API_URL}/api/standards/categories/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: reorderData })
        });
        
        if (res.ok) {
          toast.success('Categories reordered');
        } else {
          throw new Error('Failed to save order');
        }
      } catch (error) {
        console.error('Error reordering categories:', error);
        toast.error('Failed to save category order');
        // Revert on error
        fetchCategories();
      }
    }
  };

  // Handle section reorder within a category
  const handleSectionReorder = async (categoryId, newSections) => {
    // Optimistically update UI
    setSections(prev => ({ ...prev, [categoryId]: newSections }));
    
    // Save to backend
    try {
      const reorderData = newSections.map((section, index) => ({
        id: section.id,
        order: index + 1
      }));
      
      const res = await fetch(`${API_URL}/api/standards/sections/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: reorderData })
      });
      
      if (res.ok) {
        toast.success('Documents reordered');
      } else {
        throw new Error('Failed to save order');
      }
    } catch (error) {
      console.error('Error reordering sections:', error);
      toast.error('Failed to save document order');
      // Revert on error
      fetchSections(categoryId);
    }
  };

  // Category CRUD
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        icon: category.icon || 'FileText',
        access_level: category.access_level || 'public',
        order: category.order || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        icon: 'FileText',
        access_level: 'public',
        order: categories.length + 1
      });
    }
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingCategory 
        ? `${API_URL}/api/standards/categories/${editingCategory.id}`
        : `${API_URL}/api/standards/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });

      if (res.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setCategoryModalOpen(false);
        fetchCategories();
      } else {
        throw new Error('Failed to save category');
      }
    } catch (error) {
      toast.error('Failed to save category');
    }
    setSaving(false);
  };

  const toggleCategoryPublish = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/categories/${category.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !category.is_published })
      });

      if (res.ok) {
        toast.success(category.is_published ? 'Category unpublished' : 'Category published');
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  // Section CRUD
  const openSectionModal = (categoryId, section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        category_id: section.category_id,
        title: section.title,
        content: section.content,
        version: section.version || '1.0',
        order: section.order || 0
      });
    } else {
      setEditingSection(null);
      const categorySections = sections[categoryId] || [];
      setSectionForm({
        category_id: categoryId,
        title: '',
        content: '# New Document\n\nStart writing your content here...',
        version: '1.0',
        order: categorySections.length + 1
      });
    }
    setSectionModalOpen(true);
  };

  const saveSection = async () => {
    if (!sectionForm.title.trim()) {
      toast.error('Section title is required');
      return;
    }

    setSaving(true);
    try {
      // If editing and content changed, save version history first
      if (editingSection && editingSection.content !== sectionForm.content) {
        await fetch(`${API_URL}/api/standards/sections/${editingSection.id}/save-version`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            change_notes: 'Content updated',
            changed_by: JSON.parse(localStorage.getItem('admin_user'))?.name || 'Admin'
          })
        });
      }

      const url = editingSection 
        ? `${API_URL}/api/standards/sections/${editingSection.id}`
        : `${API_URL}/api/standards/sections`;
      
      const method = editingSection ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionForm)
      });

      if (res.ok) {
        toast.success(editingSection ? 'Document updated' : 'Document created');
        setSectionModalOpen(false);
        fetchSections(sectionForm.category_id);
      } else {
        throw new Error('Failed to save document');
      }
    } catch (error) {
      toast.error('Failed to save document');
    }
    setSaving(false);
  };

  const toggleSectionPublish = async (section) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/sections/${section.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !section.is_published })
      });

      if (res.ok) {
        toast.success(section.is_published ? 'Document unpublished' : 'Document published');
        fetchSections(section.category_id);
      }
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  // Delete handlers
  const openDeleteModal = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      const url = deleteTarget.type === 'category'
        ? `${API_URL}/api/standards/categories/${deleteTarget.item.id}`
        : `${API_URL}/api/standards/sections/${deleteTarget.item.id}`;
      
      const res = await fetch(url, { method: 'DELETE' });
      
      if (res.ok) {
        toast.success(`${deleteTarget.type === 'category' ? 'Category' : 'Document'} deleted`);
        setDeleteModalOpen(false);
        setDeleteTarget(null);
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Version history
  const openVersionHistory = async (section) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/sections/${section.id}/versions`);
      const data = await res.json();
      setVersionHistory(data.versions || []);
      setSelectedSection(section);
      setVersionModalOpen(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const restoreVersion = async (versionId) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/sections/${selectedSection.id}/restore/${versionId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Version restored');
        setVersionModalOpen(false);
        fetchSections(selectedSection.category_id);
      }
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  // Stats
  const stats = {
    totalCategories: categories.length,
    publicCategories: categories.filter(c => c.access_level === 'public').length,
    internalCategories: categories.filter(c => c.access_level === 'internal').length,
    totalDocuments: Object.values(sections).flat().length
  };

  if (loading) {
    return (
      <AdminSidebar pageTitle="Company Standards" pageIcon={BookOpen}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="Company Standards" pageIcon={BookOpen}>
      {/* Navigation Visibility Toggle */}
      <div className={`mb-6 p-4 rounded-xl border ${showStandardsInNav ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showStandardsInNav ? (
              <Eye className="w-5 h-5 text-green-600" />
            ) : (
              <EyeOff className="w-5 h-5 text-amber-600" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">
                Standards Menu: {showStandardsInNav ? 'Visible' : 'Hidden'}
              </h3>
              <p className="text-sm text-slate-600">
                {showStandardsInNav 
                  ? 'The "Standards" link is visible in the website header and footer.' 
                  : 'The "Standards" link is hidden from the website header and footer.'}
              </p>
            </div>
          </div>
          <Button
            variant={showStandardsInNav ? 'outline' : 'default'}
            onClick={toggleStandardsVisibility}
            disabled={savingNavSettings}
            className="min-w-[120px]"
          >
            {savingNavSettings ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : showStandardsInNav ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Menu
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Menu
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Categories</p>
              <p className="text-2xl font-bold text-slate-900">{stats.totalCategories}</p>
            </div>
            <div className="p-2 bg-cyan-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-cyan-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Public</p>
              <p className="text-2xl font-bold text-slate-900">{stats.publicCategories}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Internal</p>
              <p className="text-2xl font-bold text-slate-900">{stats.internalCategories}</p>
            </div>
            <div className="p-2 bg-amber-100 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Documents</p>
              <p className="text-2xl font-bold text-slate-900">{stats.totalDocuments}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Button onClick={() => openCategoryModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
          <Button variant="outline" asChild>
            <a href="/standards" target="_blank" rel="noopener noreferrer">
              <Eye className="w-4 h-4 mr-2" />
              View Public Page
            </a>
          </Button>
        </div>
        <Button 
          variant="outline"
          onClick={() => window.open(`${API_URL}/api/standards/pdf/all?access_level=all`, '_blank')}
        >
          <Download className="w-4 h-4 mr-2" />
          Export All (PDF)
        </Button>
      </div>

      {/* Drag and Drop Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
        <GripVertical className="w-5 h-5 text-blue-600" />
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Drag the grip handles to reorder categories and documents within each category.
        </p>
      </div>

      {/* Categories and Sections List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Categories Yet</h3>
            <p className="text-slate-500 mb-4">Create your first category to start building your standards library.</p>
            <Button onClick={() => openCategoryModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Category
            </Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y divide-slate-200">
                {categories.map((category) => (
                  <SortableCategoryItem
                    key={category.id}
                    category={category}
                    sections={sections}
                    expandedCategories={expandedCategories}
                    toggleCategory={toggleCategory}
                    openSectionModal={openSectionModal}
                    toggleCategoryPublish={toggleCategoryPublish}
                    openCategoryModal={openCategoryModal}
                    openDeleteModal={openDeleteModal}
                    onSectionReorder={handleSectionReorder}
                    openVersionHistory={openVersionHistory}
                    toggleSectionPublish={toggleSectionPublish}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details' : 'Add a new category to organize your standards'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Safety Program"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Icon</label>
                <Select 
                  value={categoryForm.icon} 
                  onValueChange={(value) => setCategoryForm(prev => ({ ...prev, icon: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Access Level</label>
                <Select 
                  value={categoryForm.access_level} 
                  onValueChange={(value) => setCategoryForm(prev => ({ ...prev, access_level: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-green-600" />
                        Public
                      </div>
                    </SelectItem>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-amber-600" />
                        Internal Only
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Section Modal */}
      <Dialog open={sectionModalOpen} onOpenChange={setSectionModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSection ? 'Edit Document' : 'Create Document'}</DialogTitle>
            <DialogDescription>
              {editingSection ? 'Update document content' : 'Add a new standards document'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input
                  value={sectionForm.title}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Safety Policy Statement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                <Input
                  value={sectionForm.version}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="1.0"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Content (Markdown) *
              </label>
              <Textarea
                value={sectionForm.content}
                onChange={(e) => setSectionForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your document content in Markdown..."
                rows={20}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Supports Markdown: # Heading, ## Subheading, **bold**, *italic*, - lists, | tables |
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSectionModalOpen(false)}>Cancel</Button>
            <Button onClick={saveSection} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={versionModalOpen} onOpenChange={setVersionModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              {selectedSection?.title} - View and restore previous versions
            </DialogDescription>
          </DialogHeader>
          
          {versionHistory.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No version history available</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
              {versionHistory.map((version) => (
                <div key={version.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Version {version.version}</span>
                      <span className="text-sm text-slate-500">
                        {new Date(version.created_at).toLocaleString()}
                      </span>
                    </div>
                    {version.change_notes && (
                      <p className="text-sm text-slate-600">{version.change_notes}</p>
                    )}
                    {version.changed_by && (
                      <p className="text-xs text-slate-400">By {version.changed_by}</p>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => restoreVersion(version.id)}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === 'category' 
                ? `This will delete the category "${deleteTarget?.item?.name}" and ALL documents within it. This action cannot be undone.`
                : `This will delete the document "${deleteTarget?.item?.title}". This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
};

export default AdminStandardsPage;
